import { useEffect, useRef, useState } from "react";
import { isTtsBusy, onTtsIdle } from "@/lib/voice";

// If the mic starts and ends within this window without producing ANY audio
// events (onspeechstart / onresult / onaudiostart), we count it as a failed
// cycle. Three failures in a row → we bail out of the continuous loop and
// expose the push-to-talk fallback. iOS Safari occasionally locks the audio
// session in a way that quietly rejects programmatic .start() — this is the
// safety net so users can still drive Kitchen View with a tap.
const FAILED_CYCLE_MS = 1500;
const FAILURE_THRESHOLD = 3;

/**
 * useVoiceRecognition — bulletproof iOS/desktop voice loop.
 *
 * Prior implementations chased ever-cleverer race conditions (TTS-idle
 * waiter queues, watchdog timers, keep-alives). Each fix added state
 * that could deadlock under a new corner case, and iOS Safari 17+ tightened
 * the audio-session model enough that any complexity now risks freezing
 * the whole main thread — users reported the entire Kitchen View
 * (including non-voice buttons) locking up after the first command.
 *
 * This rewrite is deliberately minimal and boring:
 *
 *   • ONE recogniser instance at a time. Owned by `recRef.current`.
 *   • ONE pending restart timer at a time. Owned by `restartTimerRef.current`.
 *     Before scheduling a new restart we clear the old one — no stacking.
 *   • Simple state flag `speakingBlock`: while true, DO NOT touch the mic.
 *     Set true when a command triggered TTS; cleared by the `onTtsIdle`
 *     subscription when TTS ends. No busy-loops, no polling.
 *   • On iOS, after a matched command, we abort the mic FIRST (release the
 *     audio session), then let the caller's `onCommand` handler trigger
 *     any TTS. TTS-idle re-arms the mic.
 *   • On any recogniser error/end where we should restart, we schedule
 *     exactly ONE restart with a 250ms iOS / 100ms desktop debounce.
 *   • No watchdog. No keep-alive on iOS (it interferes with the audio
 *     session). Desktop keep-alive stays but simplified.
 *
 * Contract:
 *   onCommand(text) → boolean  — return true if the utterance matched a command
 *   onInterim(text) → void
 *   onListening(bool) → void
 *   onRestarting(bool) → void
 *   onNeedsGesture() → void
 *   onBlocked() → void
 */
export function useVoiceRecognition({
  enabled,
  ready,
  onCommand,
  onInterim,
  onListening,
  onRestarting,
  onNeedsGesture,
  onBlocked,
  gestureAckedRef,
  // When true (default, back-compat for Kitchen View + wake-word loop),
  // tryMatch runs on interim transcripts too — great for short commands
  // like "next" / "stop" where latency matters. When false, we only
  // dispatch on the FINAL result so a longer multi-word query like
  // "cake recipes" isn't acted on halfway through ("cake") and leaves
  // the user watching the overlay close before they've finished speaking.
  matchInterim = true,
}) {
  const recRef = useRef(null);
  const restartTimerRef = useRef(null);
  const speakingBlockRef = useRef(false);
  const unsubTtsRef = useRef(null);
  const shouldRunRef = useRef(false);
  const cbRef = useRef({});
  cbRef.current = { onCommand, onInterim, onListening, onRestarting, onNeedsGesture, onBlocked };
  const lastMatchedRef = useRef({ text: "", ts: 0 });
  // Push-to-talk fallback state — see FAILURE_THRESHOLD notes above.
  const failureCountRef = useRef(0);
  const startedAtRef = useRef(0);
  const heardAudioRef = useRef(false);
  const [pushToTalk, setPushToTalk] = useState(false);

  useEffect(() => {
    if (!enabled || !ready) {
      teardownAll();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent || "");
    shouldRunRef.current = true;
    // Reset push-to-talk + failure counter on every fresh enable so the
    // user's manual toggle off/on is treated as a clean slate.
    failureCountRef.current = 0;
    setPushToTalk(false);

    // ---- helpers -----------------------------------------------------------
    function clearRestartTimer() {
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
    }
    function unsubTts() {
      if (unsubTtsRef.current) {
        try { unsubTtsRef.current(); } catch { /* ignore */ }
        unsubTtsRef.current = null;
      }
    }
    function killCurrentRec() {
      const r = recRef.current;
      recRef.current = null;
      if (r) {
        try { r.onresult = null; r.onerror = null; r.onend = null; } catch { /* ignore */ }
        try { r.abort(); } catch { /* ignore */ }
      }
    }
    function scheduleRestart(delay) {
      clearRestartTimer();
      if (!shouldRunRef.current) return;
      restartTimerRef.current = setTimeout(() => {
        restartTimerRef.current = null;
        startRec();
      }, delay);
    }

    // Try to match a transcript. Returns true if a command fired.
    function tryMatch(text) {
      const t = (text || "").trim().toLowerCase();
      if (!t) return false;
      const now = Date.now();
      // De-dupe interim → final echoes of the same phrase within 2.5s.
      if (t === lastMatchedRef.current.text && now - lastMatchedRef.current.ts < 2500) return true;
      const matched = cbRef.current.onCommand?.(t) === true;
      if (matched) lastMatchedRef.current = { text: t, ts: now };
      return matched;
    }

    function startRec() {
      if (!shouldRunRef.current) return;
      if (speakingBlockRef.current) return; // will be re-armed by TTS-idle
      if (recRef.current) return;           // one at a time
      let rec;
      try { rec = new SR(); } catch { return; }
      // iOS treats continuous=true as false and behaves unpredictably if we
      // ask for continuous; explicit false avoids the ambiguity.
      rec.continuous = !isIos;
      rec.interimResults = true;
      rec.maxAlternatives = 3;
      rec.lang = "en-GB";

      rec.onstart = () => {
        startedAtRef.current = Date.now();
        heardAudioRef.current = false;
        cbRef.current.onRestarting?.(false);
        cbRef.current.onListening?.(true);
      };

      rec.onresult = (e) => {
        heardAudioRef.current = true;
        failureCountRef.current = 0;
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const result = e.results[i];
          if (result.isFinal) {
            // Try each alternative for better fuzzy match on iOS
            let matched = false;
            for (let a = 0; a < result.length; a++) {
              const alt = result[a]?.transcript;
              if (alt && tryMatch(alt)) { matched = true; break; }
            }
            cbRef.current.onInterim?.("");
            if (matched && isIos) {
              // iOS command matched — tear down NOW so the audio session
              // is released before the caller's TTS starts. TTS-idle will
              // re-arm the mic. This is the load-bearing sequence.
              armSpeakingBlock();
              killCurrentRec();
              cbRef.current.onListening?.(false);
              return;
            }
            if (matched) return;
          } else {
            const interim = result[0]?.transcript || "";
            cbRef.current.onInterim?.(interim.trim());
            // iOS interim can arrive within ~500ms — match on interims too
            // so users don't wait for the (potentially 15s) final. Skipped
            // entirely when `matchInterim=false` so dictated multi-word
            // queries can finish ("cake recipes" not acted on as "cake").
            if (matchInterim && tryMatch(interim) && isIos) {
              armSpeakingBlock();
              killCurrentRec();
              cbRef.current.onListening?.(false);
              return;
            }
          }
        }
      };

      rec.onspeechstart = () => { heardAudioRef.current = true; failureCountRef.current = 0; };
      rec.onaudiostart = () => { heardAudioRef.current = true; };

      rec.onerror = (e) => {
        if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
          shouldRunRef.current = false;
          cbRef.current.onListening?.(false);
          cbRef.current.onRestarting?.(false);
          if (gestureAckedRef?.current) cbRef.current.onBlocked?.();
          else cbRef.current.onNeedsGesture?.();
          return;
        }
        // audio-capture / network / etc — mark as a failed cycle so the
        // push-to-talk fallback trips if this keeps happening.
        failureCountRef.current += 1;
      };

      rec.onend = () => {
        cbRef.current.onListening?.(false);
        cbRef.current.onInterim?.("");
        // Bump failure counter when the recogniser ended quickly without
        // hearing any real audio — a signature of iOS silently rejecting
        // the mic (locked audio session, permission revoked mid-loop).
        const elapsed = Date.now() - (startedAtRef.current || 0);
        if (!heardAudioRef.current && elapsed < FAILED_CYCLE_MS) {
          failureCountRef.current += 1;
        }
        if (recRef.current === rec) recRef.current = null;
        if (!shouldRunRef.current) return;
        if (speakingBlockRef.current) return;
        // Bail into push-to-talk fallback if we've had too many silent
        // failures in a row — the auto-restart loop clearly isn't working
        // on this device/OS combination. User can still drive Kitchen View
        // by tapping the mic button for each command.
        if (failureCountRef.current >= FAILURE_THRESHOLD) {
          shouldRunRef.current = false;
          setPushToTalk(true);
          cbRef.current.onRestarting?.(false);
          return;
        }
        cbRef.current.onRestarting?.(true);
        scheduleRestart(isIos ? 250 : 100);
      };

      recRef.current = rec;
      try {
        rec.start();
      } catch (err) {
        recRef.current = null;
        if (err?.name === "NotAllowedError") {
          shouldRunRef.current = false;
          if (gestureAckedRef?.current) cbRef.current.onBlocked?.();
          else cbRef.current.onNeedsGesture?.();
          return;
        }
        // InvalidStateError = previous instance still tearing down.
        // Any other error = try again shortly, once.
        scheduleRestart(err?.name === "InvalidStateError" ? 600 : 800);
      }
    }

    // When a command triggers TTS, we mark speakingBlock so the mic won't
    // restart while iOS's audio session is busy with the speaker. `onTtsIdle`
    // fires exactly once when TTS finishes (with a 300ms grace). We use a
    // Promise-of-idle so if TTS hasn't started yet by the time this runs,
    // we still get notified on the very next utterance's end.
    function armSpeakingBlock() {
      speakingBlockRef.current = true;
      cbRef.current.onRestarting?.(true);
      unsubTts(); // safety — avoid stacking listeners
      // Give the caller a microtask to actually kick off TTS via speak(),
      // then subscribe. If TTS isn't busy on subscribe, onTtsIdle fires
      // immediately (with the grace) — which is fine, mic re-arms fast.
      setTimeout(() => {
        // If we lost `shouldRun` (voice off called during the tick), abort.
        if (!shouldRunRef.current) { speakingBlockRef.current = false; return; }
        // If TTS never became busy at all (caller decided not to speak),
        // just re-arm immediately.
        if (!isTtsBusy()) {
          speakingBlockRef.current = false;
          scheduleRestart(150);
          return;
        }
        unsubTtsRef.current = onTtsIdle(() => {
          unsubTtsRef.current = null;
          speakingBlockRef.current = false;
          if (!shouldRunRef.current) return;
          scheduleRestart(isIos ? 250 : 100);
        });
      }, 30);
    }

    // ---- init --------------------------------------------------------------
    // Prime the mic permission with an explicit getUserMedia so Safari's
    // permission prompt is bound to the current user gesture (the click
    // that toggled voice on). Then start.
    (async () => {
      try {
        if (navigator.mediaDevices?.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch (err) {
        if (err?.name === "NotAllowedError") {
          cbRef.current.onNeedsGesture?.();
          return;
        }
      }
      startRec();
    })();

    // ---- desktop keep-alive (iOS does its own utterance-based cycling) ----
    let keepAlive = null;
    if (!isIos) {
      // Chrome/Edge continuous mode silently stops delivering results after
      // ~45s of silence. Cycle every 25s to stay under that threshold.
      keepAlive = setInterval(() => {
        const r = recRef.current;
        if (r && shouldRunRef.current && !speakingBlockRef.current) {
          try { r.stop(); } catch { /* ignore */ }
        }
      }, 25_000);
    }

    // ---- teardown ---------------------------------------------------------
    function teardownAll() {
      shouldRunRef.current = false;
      speakingBlockRef.current = false;
      clearRestartTimer();
      unsubTts();
      killCurrentRec();
      cbRef.current.onListening?.(false);
      cbRef.current.onRestarting?.(false);
    }
    return () => {
      if (keepAlive) clearInterval(keepAlive);
      teardownAll();
    };
  }, [enabled, ready]);

  // Imperative kill for callers that need to stop mid-effect (e.g. "voice off").
  const kill = () => {
    shouldRunRef.current = false;
    speakingBlockRef.current = false;
    if (restartTimerRef.current) { clearTimeout(restartTimerRef.current); restartTimerRef.current = null; }
    if (unsubTtsRef.current) { try { unsubTtsRef.current(); } catch { /* ignore */ } unsubTtsRef.current = null; }
    const r = recRef.current;
    recRef.current = null;
    if (r) {
      try { r.onresult = null; r.onerror = null; r.onend = null; } catch { /* ignore */ }
      try { r.abort(); } catch { /* ignore */ }
    }
    setPushToTalk(false);
    failureCountRef.current = 0;
  };

  /**
   * Arm the mic for ONE utterance (push-to-talk). MUST be called from
   * a direct user gesture (button click) so iOS Safari grants mic
   * permission. Used by the KitchenView fallback UI when the auto-restart
   * loop has given up.
   *
   * Success path: recognise → onCommand → tear down. User taps again
   *   for the next command.
   * Failure path: onend without match → tear down; button becomes
   *   tappable again immediately.
   *
   * We intentionally bypass `shouldRunRef` and the failure counter here —
   * push-to-talk is a manual invocation, not part of the auto-restart loop.
   */
  const armOnce = () => {
    const SR = (typeof window !== "undefined") && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    // Nuke anything in-flight so we don't stack recognisers.
    if (restartTimerRef.current) { clearTimeout(restartTimerRef.current); restartTimerRef.current = null; }
    const existing = recRef.current;
    recRef.current = null;
    if (existing) {
      try { existing.onresult = null; existing.onerror = null; existing.onend = null; } catch { /* ignore */ }
      try { existing.abort(); } catch { /* ignore */ }
    }
    let rec;
    try { rec = new SR(); } catch { return; }
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 3;
    rec.lang = "en-GB";
    rec.onstart = () => cbRef.current.onListening?.(true);
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          for (let a = 0; a < result.length; a++) {
            const alt = result[a]?.transcript;
            if (alt) { cbRef.current.onCommand?.((alt || "").trim().toLowerCase()); break; }
          }
          cbRef.current.onInterim?.("");
          try { rec.stop(); } catch { /* ignore */ }
          return;
        }
        cbRef.current.onInterim?.(result[0]?.transcript?.trim() || "");
      }
    };
    rec.onerror = () => {};
    rec.onend = () => {
      cbRef.current.onListening?.(false);
      cbRef.current.onInterim?.("");
      if (recRef.current === rec) recRef.current = null;
    };
    recRef.current = rec;
    try { rec.start(); } catch { recRef.current = null; }
  };

  return { kill, pushToTalk, armOnce };
}
